<template>
  <a-modal
    :open="!!account"
    :footer="null"
    :width="760"
    destroy-on-close
    @cancel="handleCancel"
  >
    <template #title>
      <div class="modal-title">2차 인증 · {{ accountLabel }}</div>
    </template>

    <div class="modal-content security-modal">
      <p v-if="!canSecurity" class="panel-desc" role="note">보기만 할 수 있습니다. 바꾸기는 「보안 관리」 권한을 지정받은 운영자만 할 수 있습니다.</p>
      <!-- 자기 계정은 C1 이 403(D-57)이라 상태 칸을 그리지 않는다 -->
      <a-spin v-if="!isSelf" :spinning="loadingInfo">
        <!-- 못 불러왔으면 꺼짐·수단 없음처럼 보이지 않게 상태 대신 이 줄만 보이고 쓰기 버튼을 막는다(H3) -->
        <section v-if="infoError" class="summary summary-error" role="alert">
          <span>{{ infoError }}</span>
          <a-button size="small" :loading="loadingInfo" @click="reload">다시 불러오기</a-button>
        </section>
        <!-- C1 mfa(design.md §5-3): 수단은 본인이 「내 계정 보안」에서 등록한다. 이 창은 보기·초기화만(U-25) -->
        <section v-else-if="!info" class="summary summary-loading" />
        <section v-else class="summary">
          <div class="summary-head">
            <span class="summary-name">2차 인증</span>
            <a-tag :color="sw.on ? 'green' : 'default'">{{
              sw.on ? "켜짐" : "꺼짐"
            }}</a-tag>
            <!-- 본인이 수단을 등록해 켜진(SELF) 2차 인증은 필수로 표시하지 않는다(D6) -->
            <a-tag v-if="requiredText" :color="mfa.required ? 'gold' : 'default'">{{
              requiredText
            }}</a-tag>
            <p v-if="sw.kind === 'ON' && mfa.requiredBy === 'SELF'" class="summary-desc">
              휴대폰이나 이메일이 등록돼 있어 로그인할 때 인증번호를 입력합니다.
            </p>
            <!-- A17 본인이 끈 계정(D-132): 수단은 남아 있다. 필수 계정이면 끈 값이 있어도 켜짐 -->
            <p v-else-if="sw.kind === 'OFF'" class="summary-desc">
              본인이 껐습니다 · 등록된 수단은 그대로라 본인이 「내 계정 보안」에서 다시 켤 수 있습니다.
            </p>
            <p v-else-if="mfa.selfDisabled" class="summary-desc">
              본인이 껐지만 필수 계정이라 2차 인증을 합니다.
            </p>
          </div>

          <div class="summary-block">
            <div class="summary-caption">등록된 수단</div>
            <div v-if="hasFactor" class="factor-list">
              <div v-if="mfa.phoneMasked" class="factor-row">
                <span class="factor-label">휴대폰</span>
                <span class="factor-value">{{ mfa.phoneMasked }}</span>
              </div>
              <div v-if="mfa.fallbackEmailMasked" class="factor-row">
                <span class="factor-label">이메일</span>
                <span class="factor-value">{{ mfa.fallbackEmailMasked }}</span>
              </div>
            </div>
            <p v-else class="muted">
              등록된 수단 없음 — 본인이 「내 계정 보안」에서 등록합니다.
            </p>
          </div>

          <!-- 예외·잠금은 있을 때만 한 줄씩 알리고, 없으면 한 줄로 접는다 -->
          <div class="summary-block">
            <template v-if="hasNotice">
              <a-alert
                v-if="exemptionActive"
                type="warning"
                show-icon
                class="notice"
              >
                <template #message>
                  <b>2차 인증 예외</b> {{ exemptionText }} · 승인:
                  <span class="nowrap">{{ approver(info.twoFactorExemption.approvedBy) }}</span>
                </template>
              </a-alert>
              <a-alert v-if="geoActive" type="warning" show-icon class="notice">
                <template #message>
                  <b>국외 예외</b> {{ geoText }} · 승인:
                  <span class="nowrap">{{ approver(info.geoExempt.approvedBy) }}</span>
                </template>
              </a-alert>
              <a-alert v-if="locked" type="error" show-icon class="notice">
                <template #message> <b>잠금</b> {{ locksText }} </template>
              </a-alert>
            </template>
            <p v-else class="muted">예외·잠금 없음</p>
          </div>
        </section>
      </a-spin>

      <!-- 자기 계정: 예외·초기화는 다른 운영자가 한다(D-56·D-57). 잠금 해제·이력은 허용 -->
      <a-alert
        v-if="isSelf"
        type="info"
        show-icon
        message="내 계정입니다. 본인 수단은 상단 「내 계정 보안」에서 바꿉니다. 예외·2차 인증 초기화는 다른 운영자에게 요청하세요."
      />

      <a-tabs v-model:activeKey="tab" class="tabs">
        <!-- C2·C3: 2차 인증 예외(최대 24시간, 사유 필수) -->
        <a-tab-pane key="mfa" tab="2차 인증 예외" :disabled="isSelf">
          <section class="panel">
            <h4 class="panel-title">2차 인증 예외</h4>
            <p class="panel-desc">
              예외 동안 이 계정은 인증번호 없이 로그인과 발송 전 인증을
              통과합니다. 쓸 때마다 이력에 남습니다.
            </p>
            <label class="field-label">예외 시간 (최대 24시간)</label>
            <a-input-number
              v-model:value="form.exemptHours"
              :min="1"
              :max="24"
              :precision="0"
              :disabled="writeLocked"
              addon-after="시간"
              class="hours-input"
            />
            <label class="field-label">사유</label>
            <a-textarea
              v-model:value="form.exemptReason"
              :disabled="writeLocked"
              :rows="2"
              :maxlength="200"
              placeholder="예: 카카오톡 장애로 인증번호를 받을 수 없음"
            />
            <div class="panel-actions">
              <a-button
                v-if="exemptionActive"
                danger
                :disabled="writeLocked"
                :loading="busy === 'mfa-off'"
                @click="disableExemption"
              >
                예외 끄기
              </a-button>
              <a-button
                type="primary"
                class="yellow-button"
                :disabled="writeLocked"
                :loading="busy === 'mfa-on'"
                @click="enableExemption"
              >
                예외 켜기
              </a-button>
            </div>
          </section>
        </a-tab-pane>

        <!-- C4·C5: 국외 예외(끝 시각 최대 30일·사유·증빙 필수) -->
        <a-tab-pane key="geo" tab="국외 예외" :disabled="isSelf">
          <section class="panel">
            <h4 class="panel-title">국외 예외</h4>
            <p class="panel-desc">
              예외 동안 이 계정은 해외에서도 로그인·이용할 수 있습니다.
            </p>
            <label class="field-label">끝 시각 (최대 {{ GEO_EXEMPTION_MAX_DAYS }}일)</label>
            <a-date-picker
              v-model:value="form.geoUntil"
              :disabled="writeLocked"
              :locale="locale"
              show-time
              format="YYYY-MM-DD HH:mm"
              :disabled-date="disabledGeoDay"
              placeholder="예외가 끝나는 시각"
              style="width: 100%"
            />
            <p class="field-hint">
              지금부터 {{ GEO_EXEMPTION_MAX_DAYS }}일 뒤 같은 시각까지 고를 수 있습니다.
            </p>
            <label class="field-label">사유</label>
            <a-textarea
              v-model:value="form.geoReason"
              :disabled="writeLocked"
              :rows="2"
              :maxlength="200"
              placeholder="예: 해외 출장(학회 참석)"
            />
            <label class="field-label">증빙 위치</label>
            <a-input
              v-model:value="form.geoEvidence"
              :disabled="writeLocked"
              :maxlength="300"
              placeholder="예: 출장 결재 문서 번호·링크"
            />
            <div class="panel-actions">
              <a-button
                v-if="geoActive"
                danger
                :disabled="writeLocked"
                :loading="busy === 'geo-off'"
                @click="removeGeo"
              >
                예외 해제
              </a-button>
              <a-button
                type="primary"
                class="yellow-button"
                :disabled="writeLocked"
                :loading="busy === 'geo-on'"
                @click="registerGeo"
              >
                예외 등록
              </a-button>
            </div>
          </section>
        </a-tab-pane>

        <!-- C6·A10 -->
        <a-tab-pane key="etc" tab="잠금·초기화">
          <section class="panel">
            <h4 class="panel-title">잠금 해제</h4>
            <p class="panel-desc">
              비밀번호를 5번 틀려 잠긴 접속 IP 를 모두 풀고 실패 횟수를 0으로
              돌립니다.
            </p>
            <p v-if="!isSelf" class="panel-desc">지금 잠금: {{ locksText }}</p>
            <div class="panel-actions">
              <a-button
                :disabled="writeLocked"
                :loading="busy === 'unlock'"
                @click="unlock"
                >잠금 해제</a-button
              >
            </div>
          </section>

          <section v-if="!isSelf" class="panel panel-danger">
            <div class="danger-caption">위험한 작업</div>
            <h4 class="panel-title">2차 인증 초기화</h4>
            <p class="panel-desc">
              휴대폰을 잃어버렸을 때 씁니다. 그 계정의 휴대폰·이메일 수단을 모두
              지웁니다. 운영자 계정·회사가 필수로 정한 계정은 다음 로그인 때 다시
              등록하고, 본인이 켠 계정은 2차 인증이 꺼집니다.
            </p>
            <div class="panel-actions">
              <a-button
                danger
                :disabled="writeLocked"
                :loading="busy === 'reset'"
                @click="resetTwoFactor"
                >2차 인증 초기화</a-button
              >
            </div>
          </section>
        </a-tab-pane>

        <!-- B3: 보안 이력. 가로 스크롤은 표 안에서만 -->
        <a-tab-pane key="logs" tab="이력">
          <a-table
            size="small"
            row-key="_rowKey"
            class="log-table"
            :columns="logColumns"
            :data-source="logs"
            :loading="loadingLogs"
            :pagination="false"
          >
            <template #bodyCell="{ column, record }">
              <span v-if="column.key === 'createdAt'" class="log-time">{{
                formatTime(record.createdAt)
              }}</span>
              <template v-else-if="column.key === 'action'">
                {{ ACTION_LABEL[record.action] || "기타" }}
              </template>
              <template v-else-if="column.key === 'actor'">
                {{ actorText(record) }}
              </template>
              <!-- 내용은 문장으로(D9), 접속 IP 는 그 아래 작게 -->
              <template v-else-if="column.key === 'detail'">
                <div>{{ securityLogDetail(record.action, record.detail) }}</div>
                <div v-if="record.ip" class="log-ip">IP {{ record.ip }}</div>
              </template>
            </template>
            <template #emptyText>
              <div v-if="logsError" class="noResult log-error">
                <span>{{ logsError }}</span>
                <a-button size="small" @click="loadLogs(true)">다시 불러오기</a-button>
              </div>
              <div v-else class="noResult">이력이 없습니다.</div>
            </template>
          </a-table>
          <div v-if="nextCursor" class="panel-actions">
            <a-button :loading="loadingLogs" @click="loadLogs(false)"
              >더 보기</a-button
            >
          </div>
        </a-tab-pane>
      </a-tabs>

      <div class="modal-footer">
        <a-button @click="handleCancel">닫기</a-button>
      </div>
    </div>
  </a-modal>
</template>

<script>
import { message } from "ant-design-vue";
import dayjs from "dayjs";
import locale from "ant-design-vue/es/date-picker/locale/ko_KR";
import "dayjs/locale/ko";
import apiInstance from "@/axios";
import { confirmDialog } from "@/utils/utils";
import {
  errorCode,
  errorMessage,
  formatTime,
  GEO_EXEMPTION_MAX_DAYS,
  geoUntilError,
  isGeoUntilDayDisabled,
  isSelfAccount,
  isStepUpCancelled,
  securityLogDetail,
  tokenPayload,
  twoFactorSwitch,
} from "@/utils/mfa";
import { loadOperatorNames } from "@/utils/operators";
import { canNow } from "@/utils/perm";

// account_security_logs.action (design.md §8-4, v0.3 뜻)
const ACTION_LABEL = {
  TWO_FACTOR_ASSIGN: "휴대폰 등록·변경",
  TWO_FACTOR_REMOVE: "휴대폰 삭제",
  TWO_FACTOR_FIRST_VERIFY: "휴대폰 첫 확인",
  FALLBACK_EMAIL_SET: "이메일 등록·변경·삭제",
  OTP_SENT: "인증번호 발송",
  OTP_SEND_FAIL: "인증번호 발송 실패",
  MFA_SUCCESS: "2차 인증 성공",
  MFA_FAIL: "2차 인증 실패",
  SEND_AUTH_SUCCESS: "발송 전 인증 성공",
  SEND_AUTH_FAIL: "발송 전 인증 실패",
  MFA_EXEMPT_ON: "2차 인증 예외 켬",
  MFA_EXEMPT_OFF: "2차 인증 예외 끔",
  MFA_EXEMPT_USED: "2차 인증 예외 사용",
  GEO_EXEMPT_ON: "국외 예외 등록",
  GEO_EXEMPT_OFF: "국외 예외 해제",
  GEO_BLOCKED: "국외 접속 차단",
  GEO_ALLOW_IP_ADDED: "국외 허용 IP 추가",
  GEO_ALLOW_IP_UPDATED: "국외 허용 IP 수정",
  GEO_ALLOW_IP_REMOVED: "국외 허용 IP 삭제",
  GEO_MODE_CHANGED: "해외 접속 차단 모드 변경",
  LOCK: "잠김",
  UNLOCK: "잠금 해제",
  POLICY_ON: "회사 2차 인증 필수 켬",
  POLICY_OFF: "회사 2차 인증 필수 끔",
  ACCOUNT_CREATE: "계정 생성",
  ACCOUNT_UPDATE: "계정 수정",
  ACCOUNT_DELETE: "계정 삭제",
  PASSWORD_RESET: "비밀번호 초기화",
  PERMISSION_CHANGE: "권한 변경",
  STUDIO_MENU_CHANGE: "메뉴 변경",
  TWO_FACTOR_RESET: "2차 인증 초기화",
  TWO_FACTOR_SELF_DISABLED: "본인이 2차 인증 끔",
  TWO_FACTOR_SELF_ENABLED: "본인이 2차 인증 켬",
  WHITELIST_LOGIN: "공용 비밀번호 로그인",
  WHITELIST_PW_MODE: "공용 비밀번호 설정 변경",
};

// 이력 detail.by — 누가 했는지(본인 등록·관리자/운영자 초기화, D-116 리셀러)
const BY_LABEL = {
  SELF: "본인",
  MANAGER: "관리자",
  OPERATOR: "운영자",
  RESELLER: "리셀러",
};

// mfa.requiredBy (design.md §5-3). SELF(본인이 수단 등록)는 필수로 표시하지 않는다(D6)
const REQUIRED_BY_LABEL = {
  OPERATOR: "운영자 계정",
  COMPANY: "회사 전체(운영자 설정)",
};

// 보안 상태·이력 불러오기 실패 알림은 한 번만(같은 key, H3)
const LOAD_ERROR_KEY = "account-security-load";

const LOG_PAGE_SIZE = 20;

const emptyForm = () => ({
  exemptHours: 24,
  exemptReason: "",
  geoUntil: null,
  geoReason: "",
  geoEvidence: "",
});

const isActive = (item) => !!item && dayjs(item.until).isAfter(dayjs());

/**
 * 「2차 인증」 창(REQ-ACC-012). 병원·제조사(기공소)·리셀러·운영자 계정 화면의 행과 STUDIO 정보 창의 계정 카드에서 연다.
 * 운영자 API(design.md §6-3 C1~C6)·B3 이력·A10 초기화를 부른다. 수단(휴대폰·이메일)은 본인만 등록하므로
 * 이 창에서는 상태 보기와 초기화만 한다(U-25).
 * 증표가 필요한 호출은 axios 인터셉터가 step-up 창을 띄우고 다시 보낸다.
 */
export default {
  name: "AccountSecurityModal",
  props: {
    // 목록 행(_id 필수). null 이면 창이 닫힌다.
    account: { type: Object, default: null },
  },
  emits: ["close"],
  data() {
    return {
      ACTION_LABEL,
      GEO_EXEMPTION_MAX_DAYS,
      formatTime,
      securityLogDetail,
      locale,
      tab: "mfa",
      info: null,
      infoError: "",
      loadingInfo: false,
      logs: [],
      logsError: "",
      nextCursor: null,
      loadingLogs: false,
      busy: "",
      form: emptyForm(),
      operatorNames: {},
      logColumns: [
        { key: "createdAt", title: "시각", width: 132 },
        { key: "action", title: "작업", width: 150 },
        { key: "actor", title: "실행자", width: 130 },
        { key: "detail", title: "내용" },
      ],
    };
  },
  computed: {
    accountId() {
      return this.account && this.account._id;
    },
    accountLabel() {
      const a = this.account || {};
      return [a.corpName, a.name, a.username].filter(Boolean).join(" · ");
    },
    // 로그인한 운영자 자신의 행이면 상태 조회·예외·초기화를 막는다(D-56·D-57)
    isSelf() {
      return isSelfAccount(
        this.account,
        tokenPayload(localStorage.getItem("accessTokenCerti"))
      );
    },
    mfa() {
      return (this.info && this.info.mfa) || {};
    },
    // 상태를 모르면(불러오는 중·실패) 쓰기 버튼을 막는다(H3). 자기 계정은 상태를 읽지 않으므로 잠금 해제만 허용
    // 쓰기(예외·잠금 해제·초기화)는 서버가 ops.security.manage 지정자만 받음(@PermStrict) — 없으면 잠금(10-30)
    canSecurity() {
      return canNow(this.$store.state.introspect, "ops.security.manage");
    },
    writeLocked() {
      return !this.canSecurity || (!this.isSelf && !this.info);
    },
    // 확인창에 쓰는 대상: 정다운(jdw01)
    accountName() {
      const a = this.account || {};
      return a.name ? `${a.name}(${a.username})` : a.username || "이 계정";
    },
    exemptionActive() {
      return isActive(this.info && this.info.twoFactorExemption);
    },
    geoActive() {
      return isActive(this.info && this.info.geoExempt);
    },
    hasFactor() {
      return !!(this.mfa.phoneMasked || this.mfa.fallbackEmailMasked);
    },
    // 켜짐 = 수단 있음 && (본인이 안 끔 || 운영자·회사 강제) — 「내 계정 보안」 스위치와 같은 판정
    sw() {
      return twoFactorSwitch(this.mfa);
    },
    locked() {
      return !!(this.info && this.info.locks && this.info.locks.length);
    },
    hasNotice() {
      return this.exemptionActive || this.geoActive || this.locked;
    },
    requiredText() {
      if (!this.mfa.required) return "필수 아님";
      if (this.mfa.requiredBy === "SELF") return "";
      const by = REQUIRED_BY_LABEL[this.mfa.requiredBy];
      return by ? `필수 · ${by}` : "필수";
    },
    exemptionText() {
      if (!this.exemptionActive) return "없음";
      const e = this.info.twoFactorExemption;
      return `${formatTime(e.until)}까지 · 사유: ${e.reason}`;
    },
    geoText() {
      if (!this.geoActive) return "없음";
      const g = this.info.geoExempt;
      return `${formatTime(g.until)}까지 · 사유: ${g.reason} · 증빙: ${g.evidenceRef}`;
    },
    locksText() {
      const locks = (this.info && this.info.locks) || [];
      if (!locks.length) return "없음";
      return locks
        .map((l) => `${l.ip} (${formatTime(l.lockedUntil)}까지)`)
        .join(", ");
    },
  },
  watch: {
    account: {
      immediate: true,
      handler(account) {
        if (!account) return;
        this.tab = this.isSelf ? "etc" : "mfa";
        this.form = emptyForm();
        this.info = null;
        loadOperatorNames().then((names) => (this.operatorNames = names));
        this.reload().then(() => {
          // 잠긴 계정이면 잠금 해제가 있는 탭으로 연다(M6)
          if (this.locked && this.tab === "mfa") this.tab = "etc";
        });
      },
    },
  },
  methods: {
    handleCancel() {
      this.$emit("close");
    },
    // 오늘 전·30일 뒤 다음 날부터는 달력에서 막는다
    disabledGeoDay(current) {
      return isGeoUntilDayDisabled(current);
    },
    // 승인 운영자: 운영자 목록에서 찾은 이름(아이디), 못 찾으면 서버가 준 id (M1)
    approver(id) {
      return this.operatorNames[id] || id || "-";
    },
    // 실행자 칸: 이름(없으면 id) + detail.by(본인/관리자/운영자)
    actorText(record) {
      const name = record.actorName || record.actorAccountId || "-";
      const by = record.detail && BY_LABEL[record.detail.by];
      return by ? `${name} (${by})` : name;
    },
    // 불러오기 실패 문구(what 은 조사까지: "보안 상태를"). 본인 확인 창을 스스로 닫았으면 알림 없이 창 안에만(D5), 아니면 알림 한 번
    loadFailed(error, what) {
      console.error(error);
      if (isStepUpCancelled(error)) {
        return `본인 확인을 취소해 ${what} 불러오지 못했습니다.`;
      }
      const text = errorMessage(error, `${what} 불러오지 못했습니다.`);
      message.error({ content: text, key: LOAD_ERROR_KEY });
      return text;
    },
    reload() {
      return Promise.all([this.loadInfo(), this.loadLogs(true)]);
    },
    async loadInfo() {
      // C1 은 자기 계정이면 403(D-57). 부르지 않는다
      if (this.isSelf) return;
      this.loadingInfo = true;
      this.infoError = "";
      try {
        const { data } = await apiInstance.get(
          `/admin/security/accounts/${this.accountId}`,
          {
            showGlobalLoading: false,
          }
        );
        this.info = data;
      } catch (error) {
        this.info = null;
        this.infoError = this.loadFailed(error, "보안 상태를");
      } finally {
        this.loadingInfo = false;
      }
    },
    async loadLogs(reset) {
      this.loadingLogs = true;
      this.logsError = "";
      try {
        const params = { limit: LOG_PAGE_SIZE };
        if (!reset && this.nextCursor) params.cursor = this.nextCursor;
        const { data } = await apiInstance.get(
          `/accounts/${this.accountId}/security-logs`,
          {
            params,
            showGlobalLoading: false,
          }
        );
        const offset = reset ? 0 : this.logs.length;
        const items = (data.items || []).map((item, i) => ({
          ...item,
          _rowKey: offset + i,
        }));
        this.logs = reset ? items : [...this.logs, ...items];
        this.nextCursor = data.nextCursor || null;
      } catch (error) {
        if (reset) this.logs = [];
        this.logsError = this.loadFailed(error, "보안 이력을");
      } finally {
        this.loadingLogs = false;
      }
    },
    // 작업 하나를 실행하고 상태·이력을 다시 읽는다
    async run(name, request, successText) {
      this.busy = name;
      try {
        await request();
        message.success(successText);
        this.form = emptyForm();
        await this.reload();
      } catch (error) {
        console.error(error);
        // 본인 확인 창을 스스로 닫은 것이면 하던 작업을 안 했을 뿐이라 실패 알림을 띄우지 않는다(D5)
        if (isStepUpCancelled(error)) return;
        // GEO_BLOCKED 는 axios 인터셉터가 이미 같은 key 로 알렸다 — 같은 key 로 덮어 한 번만 보이게 한다
        message.error({
          content: errorMessage(
            error,
            "처리하지 못했습니다. 입력값을 확인하고 다시 시도하세요."
          ),
          key: errorCode(error) === "GEO_BLOCKED" ? "GEO_BLOCKED" : undefined,
        });
      } finally {
        this.busy = "";
      }
    },
    enableExemption() {
      const hours = this.form.exemptHours;
      const reason = this.form.exemptReason.trim();
      if (!Number.isInteger(hours) || hours < 1 || hours > 24) {
        message.warning("예외 시간은 1~24시간 사이로 입력하세요.");
        return;
      }
      if (!reason) {
        message.warning("예외 사유를 입력하세요.");
        return;
      }
      this.run(
        "mfa-on",
        () =>
          apiInstance.post(
            `/admin/security/accounts/${this.accountId}/two-factor-exemption`,
            {
              hours,
              reason,
            }
          ),
        `2차 인증 예외를 ${hours}시간 동안 켰습니다.`
      );
    },
    async disableExemption() {
      const ok = await confirmDialog(
        "2차 인증 예외 끄기",
        `${this.accountName}님의 2차 인증 예외를 끕니다. 다음 로그인부터 인증번호를 다시 확인합니다.`,
        { confirmText: "끄기" }
      );
      if (!ok) return;
      this.run(
        "mfa-off",
        () =>
          apiInstance.delete(
            `/admin/security/accounts/${this.accountId}/two-factor-exemption`
          ),
        "2차 인증 예외를 껐습니다."
      );
    },
    registerGeo() {
      const { geoUntil } = this.form;
      const reason = this.form.geoReason.trim();
      const evidenceRef = this.form.geoEvidence.trim();
      const untilError = geoUntilError(geoUntil);
      if (untilError) {
        message.warning(untilError);
        return;
      }
      if (!reason) {
        message.warning("예외 사유를 입력하세요.");
        return;
      }
      if (!evidenceRef) {
        message.warning("증빙 위치(결재 문서 번호·링크 등)를 입력하세요.");
        return;
      }
      this.run(
        "geo-on",
        () =>
          apiInstance.post(
            `/admin/security/accounts/${this.accountId}/geo-exemption`,
            {
              until: geoUntil.toISOString(),
              reason,
              evidenceRef,
            }
          ),
        `${formatTime(geoUntil)}까지 국외 예외를 등록했습니다.`
      );
    },
    async removeGeo() {
      const ok = await confirmDialog(
        "국외 예외 해제",
        `${this.accountName}님의 국외 예외를 해제합니다. 해제하면 이 계정은 해외에서 바로 로그인할 수 없습니다.`,
        { confirmText: "해제" }
      );
      if (!ok) return;
      this.run(
        "geo-off",
        () =>
          apiInstance.delete(
            `/admin/security/accounts/${this.accountId}/geo-exemption`
          ),
        "국외 예외를 해제했습니다."
      );
    },
    unlock() {
      this.run(
        "unlock",
        () =>
          apiInstance.post(`/admin/security/accounts/${this.accountId}/unlock`),
        "잠금을 풀었습니다."
      );
    },
    async resetTwoFactor() {
      // 대상(이름(아이디))과 결과를 말하고 확인 버튼은 빨간 「초기화」(D7, M5).
      // 수단을 지운 뒤에도 필수인 건 운영자·회사 강제뿐 — 본인이 켠(SELF) 계정은 꺼진다(inapi requirementOf)
      const reEnroll = ["OPERATOR", "COMPANY"].includes(this.mfa.requiredBy);
      const ok = await confirmDialog(
        "2차 인증 초기화",
        `${this.accountName}님의 2차 인증을 초기화합니다. 등록된 휴대폰·이메일이 지워지고, ${
          reEnroll ? "다음 로그인 때 다시 등록합니다." : "2차 인증이 꺼집니다."
        }`,
        { confirmText: "초기화", danger: true }
      );
      if (!ok) return;
      this.run(
        "reset",
        () => apiInstance.delete(`/auth/2fa/admin/reset/${this.accountId}`),
        `${this.accountName}님의 2차 인증을 초기화했습니다.`
      );
    },
  },
};
</script>

<style scoped>
.modal-title {
  font-size: 16px;
  font-weight: bold;
}
/* 창 안 여백·닫기 줄은 전역 .security-modal(ant-design.scss) */

/* 상단 요약: 상태 머리 한 줄 + 등록된 수단 + 예외·잠금 */
.summary {
  padding: 16px 20px;
  border-radius: 12px;
  background: #f7f8f9;
}
.summary-head {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}
.summary-error {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  border: 1px solid #f5a39d;
  background: #fff8f7;
  font-size: 14px;
  color: #a8071a;
}
.summary-loading {
  min-height: 120px;
}
.summary-desc {
  flex-basis: 100%;
  margin: 0;
  font-size: 13px;
  color: #4b5158;
}
.summary-name {
  margin-right: 4px;
  font-size: 15px;
  font-weight: 700;
  color: #17191c;
}
.summary-head :deep(.ant-tag) {
  margin: 0;
  font-size: 13px;
  line-height: 22px;
}
.summary-block {
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px solid #e8ebed;
}
.summary-caption {
  margin-bottom: 6px;
  font-size: 12px;
  color: #5c6168;
}
.factor-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 28px;
}
.factor-row {
  display: flex;
  gap: 10px;
  font-size: 14px;
}
.factor-label {
  color: #5c6168;
}
.factor-value {
  font-weight: 600;
  color: #17191c;
}
.muted {
  margin: 0;
  font-size: 13px;
  color: #5c6168;
}
.notice + .notice {
  margin-top: 8px;
}
.notice b {
  margin-right: 6px;
}
.nowrap {
  white-space: nowrap;
}
/* 다른 화면의 전역 .ant-tabs-nav { padding: 0 24px } 를 끄고 탭 이름도 같은 여백 선에 맞춘다 */
.tabs {
  margin-top: 12px;
}
.modal-content :deep(.ant-tabs-nav) {
  padding: 0;
}

/* 탭 섹션: 제목·설명·입력·버튼을 한 덩어리로 */
.panel {
  padding: 16px 20px;
  border: 1px solid #e8ebed;
  border-radius: 12px;
}
.panel + .panel {
  margin-top: 12px;
}
/* 테마 h4 의 좁은 자간이 띄어쓰기를 먹어 "2차인증예외"로 붙어 보인다 */
.panel-title {
  margin: 0;
  font-size: 15px;
  font-weight: 700;
  letter-spacing: normal;
  color: #17191c;
}
.panel-desc {
  margin: 6px 0 0;
  font-size: 13px;
  color: #6e747c;
  line-height: 1.5;
}
.field-label {
  display: block;
  margin: 14px 0 6px;
  font-size: 13px;
  font-weight: 600;
  color: #2f3337;
}
.hours-input {
  width: 160px;
}
.field-hint {
  margin: 6px 0 0;
  font-size: 12px;
  color: #5c6168;
}
.panel-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 16px;
}
.panel-danger {
  border-color: #f5a39d;
  background: #fff8f7;
}
.danger-caption {
  margin-bottom: 4px;
  font-size: 12px;
  font-weight: 700;
  color: #f55247;
}
.panel-danger .panel-title {
  color: #d9363e;
}

.log-table {
  border: 1px solid #e8ebed;
  border-radius: 12px;
  overflow: hidden;
}
.log-table :deep(td) {
  word-break: keep-all;
}
.log-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}
.log-time {
  white-space: nowrap;
}
.log-ip {
  margin-top: 2px;
  font-size: 12px;
  color: #5c6168;
}

:deep(.yellow-button) {
  background-color: #ffcc32 !important;
  border-color: #ffcc32 !important;
  color: #000 !important;
}
:deep(.yellow-button:hover) {
  background-color: #e6b82e !important;
  border-color: #e6b82e !important;
}
/* 막힌 주 버튼은 회색으로(노란 그대로면 눌리는 버튼처럼 보인다) — OtpStep 과 같은 색 */
:deep(.yellow-button:disabled) {
  background-color: #e8ebed !important;
  border-color: #e8ebed !important;
  color: #4b5158 !important;
}
</style>
